import React, { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { ISSUABLE_TYPES, PRODUCTS } from "../../config";
import { useSession } from "../../auth/AuthProvider";
import { downloadCsv, toCsv, todayStamp } from "../../lib/licenses";
import { generateLicenses } from "../../lib/operations";
import { monoFont } from "../../theme";
import CustomerPicker from "../../components/CustomerPicker";
import { useAdminData } from "./AdminData";

const MAX_BATCH = 500;

// Stock keys, e.g. for LiFE's direct customers. Keys for distributors should
// normally be allocated as quota instead, so they issue them on demand.
// Without a `product` prop the dialog asks for one.
export default function GenerateKeysDialog({
  open,
  product: fixedProduct,
  initialCustomer,
  onClose,
}) {
  const session = useSession();
  const { distributors } = useAdminData();
  const [productId, setProductId] = useState(PRODUCTS[0].id);
  const [customer, setCustomer] = useState(null);
  const [count, setCount] = useState("10");
  const [development, setDevelopmentFlag] = useState(false);
  const [type, setType] = useState("Annual");
  const [label, setLabel] = useState("");
  const [region, setRegion] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [created, setCreated] = useState(null);

  useEffect(() => {
    if (open) {
      setError(null);
      setCreated(null);
      setCustomer(initialCustomer || null);
      if (initialCustomer) setCount("1");
    }
    // Only reset when the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const product = fixedProduct || PRODUCTS.find((p) => p.id === productId);
  const n = Number(count);
  const valid = Number.isInteger(n) && n >= 1 && n <= MAX_BATCH;

  async function submit(event) {
    event.preventDefault();
    if (!valid) return;
    setBusy(true);
    setError(null);
    try {
      const keys = await generateLicenses({
        productId: product.id,
        type,
        count: n,
        distributorLabel: label,
        region,
        customer,
        development,
        actor: session.email,
      });
      setCreated(keys);
    } catch (e) {
      console.error(e);
      setError(e.message || "Couldn't generate keys.");
    } finally {
      setBusy(false);
    }
  }

  const csv = () =>
    downloadCsv(
      `${product.path}-${type.toLowerCase()}-${created.length}-keys-${todayStamp()}.csv`,
      toCsv(
        created.map((key) => ({ key })),
        [
          { label: "Key", value: (r) => r.key },
          { label: "Product", value: () => product.name },
          { label: "Type", value: () => type },
          { label: "Distributor", value: () => label },
          { label: "Region", value: () => region },
        ],
      ),
    );

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="sm"
    >
      {created ? (
        <>
          <DialogTitle>{created.length} keys created</DialogTitle>
          <DialogContent>
            <Box
              sx={{
                fontFamily: monoFont,
                fontSize: 14,
                bgcolor: "background.default",
                border: 1,
                borderColor: "divider",
                borderRadius: 2,
                p: 2,
                maxHeight: 280,
                overflowY: "auto",
                columnWidth: 180,
              }}
            >
              {created.map((key) => (
                <div key={key}>{key}</div>
              ))}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 1.5 }}>
            <Button
              color="inherit"
              onClick={() => navigator.clipboard.writeText(created.join("\n"))}
            >
              Copy all
            </Button>
            <Button color="inherit" onClick={csv}>
              Download CSV
            </Button>
            <Button variant="contained" onClick={onClose}>
              Done
            </Button>
          </DialogActions>
        </>
      ) : (
        <Box component="form" onSubmit={submit}>
          <DialogTitle>
            Generate {fixedProduct ? `${fixedProduct.name} ` : ""}keys
          </DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              For LiFE's own sales. To give a distributor keys, allocate quota
              from the Distributors page instead — they then issue keys one at a
              time to their customers.
            </Alert>
            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: { sm: "1fr 1fr" },
              }}
            >
              {!fixedProduct && (
                <TextField
                  select
                  label="Product"
                  value={productId}
                  onChange={(e) => setProductId(e.target.value)}
                  sx={{ gridColumn: { sm: "1 / -1" } }}
                >
                  {PRODUCTS.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              <Box sx={{ gridColumn: { sm: "1 / -1" } }}>
                <CustomerPicker
                  owner={null}
                  value={customer}
                  onChange={setCustomer}
                  label="For a direct customer (optional)"
                  helperText="Leave empty for unassigned stock"
                />
              </Box>
              <TextField
                label="Number of keys"
                type="number"
                value={count}
                onChange={(e) => setCount(e.target.value)}
                error={count !== "" && !valid}
                helperText={`1–${MAX_BATCH}`}
                slotProps={{ htmlInput: { min: 1, max: MAX_BATCH } }}
                required
              />
              <TextField
                select
                label="Type"
                value={type}
                onChange={(e) => setType(e.target.value)}
              >
                {ISSUABLE_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t === "Trial"
                      ? "Trial (1 month)"
                      : t === "Annual"
                        ? "Annual (12 months)"
                        : t}
                  </MenuItem>
                ))}
              </TextField>
              <Autocomplete
                freeSolo
                options={distributors.map((d) => d.name)}
                inputValue={label}
                onInputChange={(_, value) => setLabel(value)}
                renderInput={(params) => (
                  <TextField {...params} label="Distributor label (optional)" />
                )}
              />
              <TextField
                label="Region (optional)"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
              />
            </Box>
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Checkbox
                  checked={development}
                  onChange={(e) => setDevelopmentFlag(e.target.checked)}
                />
              }
              label="Development keys (for LiFE's own testing; not counted)"
            />
            <Typography
              variant="caption"
              color="text.secondary"
              component="p"
              sx={{ mt: 1 }}
            >
              The label is for reference only and does not give a distributor
              portal access to these keys.
            </Typography>
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 1.5 }}>
            <Button color="inherit" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={!valid || busy}>
              {busy ? "Generating…" : `Generate ${valid ? n : ""} keys`}
            </Button>
          </DialogActions>
        </Box>
      )}
    </Dialog>
  );
}
